import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { API_SERVICE } from '@/components/candidate-job-slider-v1/api';
import { CandidateContext } from '@/components/candidate-job-slider-v1/context/index';
import { ApplicationData } from '@/components/candidate-job-slider-v1/types/application.interface';
import { useShowToast } from '@/hooks';

const SUPPORT_ERROR_MESSAGE =
  'Something went wrong. Please contact support@kula.ai if this continues.';

function getResponseStatus(error: unknown): number {
  const err = error as { response?: { status?: number } };
  return err?.response?.status ?? 0;
}

function isServerError(error: unknown): boolean {
  const status = getResponseStatus(error);
  return status >= 500 && status < 600;
}

export const useApplicationHelper = () => {
  const context = useContext(CandidateContext) as {
    transferredApplicationOverride?: {
      applicationData: any;
      refetchApplication: () => void;
    } | null;
  };
  const applicationId = window.sessionStorage.getItem('candidate-app-id');
  const queryClient = useQueryClient();
  const toast = useShowToast();
  const hasShownErrorToast = useRef(false);

  useEffect(() => {
    hasShownErrorToast.current = false;
  }, [applicationId]);

  const queryKey = [`application/${applicationId}`];
  const {
    data: applicationDataFromQuery,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery<ApplicationData>({
    queryKey,
    queryFn: () =>
      API_SERVICE.getApplication(applicationId, {
        include: ['stage_actions'],
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled:
      !context?.transferredApplicationOverride &&
      applicationId !== 'null' &&
      !!applicationId,
    retry: (failureCount, err) =>
      isServerError(err) ? false : failureCount < 3,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (
      isError &&
      error &&
      isServerError(error) &&
      !hasShownErrorToast.current
    ) {
      hasShownErrorToast.current = true;
      toast({ title: SUPPORT_ERROR_MESSAGE, status: 'error' });
    }
  }, [isError, error, toast]);

  const refetchApplication = async () => {
    if (context?.transferredApplicationOverride) {
      context.transferredApplicationOverride.refetchApplication();
      return;
    }
    if (!applicationId) return;
    hasShownErrorToast.current = false;
    await queryClient.invalidateQueries({ queryKey });
  };

  const clearPreviousApplicationCache = (previousID: string) => {
    if (!previousID) return;
    hasShownErrorToast.current = false;
    queryClient.removeQueries({ queryKey: [`application/${previousID}`] });
  };

  if (context?.transferredApplicationOverride) {
    return {
      applicationData: {
        ...context.transferredApplicationOverride.applicationData,
        transferred: true,
      },
      isLoading: false,
      isFetching: false,
      refetchApplication,
      clearPreviousApplicationCache,
    };
  }

  return {
    applicationData: applicationDataFromQuery,
    isLoading,
    isFetching,
    isError,
    isServerError: isError && isServerError(error),
    refetchApplication,
    clearPreviousApplicationCache,
  };
};
